"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Building2,
  Sparkles,
  Brain,
  Mic,
  ArrowRight,
  Target,
  Heart,
  ShieldCheck,
  Scale,
  Compass,
  Activity,
  Briefcase,
  Lock,
  Layers,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const PRINCIPLES = [
  { title: "Diagnostic Governance", body: "The board must understand the organization&apos;s actual condition — not the condition presented in quarterly reports, but the operational reality beneath the metrics. Governance grounded in forensic understanding." },
  { title: "Protocol-Driven Oversight", body: "Governance mechanisms must be systematic, not episodic. Standing mechanisms — checklists, decision frameworks, escalation protocols, accountability structures — that produce consistent governance outcomes regardless of who occupies the boardroom." },
  { title: "Outcome Accountability", body: "The board is measured by outcomes, not activities. A board that meets its schedule and reviews its reports has accomplished nothing if the organization fails to execute. Board effectiveness measured by organizational performance." },
  { title: "Cross-Domain Deliberation", body: "The most dangerous governance failures occur in the spaces between domains. The board brings multiple domains of expertise to every governance discussion — not as a luxury, but as a structural requirement." },
  { title: "Continuous Adaptation", body: "Governance is not a fixed architecture. It must evolve with the organization&apos;s challenges, the regulatory landscape, and the operational environment. The board monitors its own governance effectiveness with the same rigor it applies to the organization." },
];

const CONTRIBUTIONS = [
  {
    icon: Activity,
    title: "Operational Excellence Perspective",
    body: "Most boards govern through compliance frameworks. Board service governs through operational metrics — translating oversight into measurable execution outcomes. Board discussions grounded in operational reality, not strategic abstractions or quarterly narratives. Risk assessments informed by sovereign-scale experience. Governance mechanisms that drive organizational performance rather than merely monitoring it.",
    accent: "red",
  },
  {
    icon: Brain,
    title: "Cross-Domain Insight",
    body: "Where most board members bring single-domain expertise, the contribution spans the Triple Threat — the clinical method of a physician, the systems architecture of a technology operator, the strategic discipline of a political operator. One operating method applied across three domains, producing insights that single-domain experts cannot generate. Operational, technological, political, and human dimensions considered simultaneously.",
    accent: "purple",
  },
  {
    icon: Heart,
    title: "The 70% Operating Model",
    body: "A decade of operational data proving that teams composed of over 70% women produce the most reliable outcomes. This evidence is brought to board composition discussions, replacing ideology with operational data. The 70% Operating Model informs governance perspective on talent strategy, team composition, leadership pipeline development, and organizational culture — as operational mechanisms, not aspirational goals.",
    accent: "emerald",
  },
  {
    icon: ShieldCheck,
    title: "Risk Discipline",
    body: "The clinical method for risk governance — systematic identification, evidence-based triage, protocol-driven management, and continuous monitoring. Applied across operational, technological, financial, reputational, regulatory, and strategic risk. The structural risk discipline that has produced zero security incidents across fifteen-plus years of sovereign-level engagements — environments where the margin for error is zero.",
    accent: "cyan",
  },
];

const BOARD_TYPES = [
  {
    title: "Public Company Boards",
    body: "Translating regulatory compliance into operational performance, ensuring governance mechanisms serve the enterprise rather than merely satisfying external requirements. Operational excellence perspective drives execution outcomes. Risk discipline protects shareholder value with clinical precision.",
    accent: "red",
  },
  {
    title: "Private Company Boards",
    body: "Building governance infrastructure that scales with the organization — governance that does not constrain operational agility while providing the oversight structures that protect the enterprise as it grows. The cross-domain insight is particularly valuable for private companies navigating the transition from founder-led to professionally operated.",
    accent: "purple",
  },
  {
    title: "Family Office Boards",
    body: "Governance mechanisms that protect family wealth, ensure intergenerational transfer, and maintain operational discipline across complex, multi-jurisdictional asset structures. National security level confidentiality, compartmentalized operations, zero information leakage as foundational.",
    accent: "cyan",
  },
  {
    title: "Sovereign & Government Boards",
    body: "Governance transformation, digital governance infrastructure, and the operational discipline required to execute at national scale. Sovereign-scale experience deployed across eighteen countries processing data for over nine hundred million citizens provides governance perspective grounded in operational reality.",
    accent: "emerald",
  },
  {
    title: "Nonprofit & International Organization Boards",
    body: "Governance frameworks capable of measuring impact, ensuring transparency, and sustaining organizational effectiveness under resource constraints. Translating mission into measurable outcomes. The outcome accountability principle — the same standard that governs medical practice — provides evaluation rigor.",
    accent: "amber-gold",
  },
];

const GOVERNANCE_DOMAINS = [
  { title: "Strategy Governance", body: "Evaluating strategic proposals with operational rigor — not through intuitive assessment, but through the clinical method of diagnosis, evidence-based evaluation, and outcome prediction grounded in operational data." },
  { title: "Operations Governance", body: "Evaluating operational performance through the clinical lens: uptime, execution speed, protocol adherence, error rates, and the systemic patterns that produce operational outcomes." },
  { title: "Risk Governance", body: "Operationalizing the board&apos;s fiduciary obligation through clinical discipline. Structures risk oversight across operational, technology, reputational, regulatory, and strategic risk dimensions." },
  { title: "Diversity & Composition Governance", body: "Applying the 70% Operating Model to board evaluation — assessing whether the board&apos;s composition provides the diversity of perspective, experience, and capability the organization&apos;s challenges demand." },
  { title: "Technology & AI Governance", body: "Providing the cross-domain literacy required to govern organizations dependent on technology infrastructure. Evaluating AI deployment, data strategy, and digital risk with the precision sovereign-scale operations require." },
];

export default function BoardServicePage() {
  return (
    <>
      <PageHero
        icon={Building2}
        overline="Service · Board Service"
        overlineClassName="text-cyan"
        title={
          <>
            Sovereign-Grade <span className="text-gradient-premium">Governance Oversight.</span>
          </>
        }
        subtitle="Board and advisory-board positions for organizations seeking the operational discipline and governance expertise grounded in sovereign-scale experience. Governance built on operational reality — not compliance checklists, not theoretical frameworks, not decorative governance designed to satisfy regulators while failing to protect the enterprise."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Board Service" }]}
        badge="Governance that drives performance"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Governance is not compliance. Governance is operational architecture.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Most boards operate under a compliance paradigm. They monitor adherence to rules. They approve budgets. They review quarterly reports. They execute fiduciary duty as a legal obligation rather than an operational discipline. This paradigm produces boards that function within regulatory frameworks while failing to protect the enterprise from the operational risks that actually threaten it.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The board service inverts this paradigm. Governance is the operational architecture that ensures the organization executes at the level its challenges demand. The board is not a review body. It is the strategic operating layer that translates vision into verified outcomes. Every governance mechanism introduced to the boardroom serves a specific operational purpose: to close the gap between what the organization plans and what the organization achieves.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations seeking board-level governance that translates directly into execution outcomes — not governance that satisfies regulatory requirements while leaving operational gaps unaddressed — find this board service transformative. The contribution is not advisory decoration. It is operational governance — the application of a physician&apos;s clinical method to the oversight responsibilities that boards carry.
            </p>
          </div>

          <div className="space-y-3">
            {PRINCIPLES.map((p, i) => {
              const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
              const a = ACCENT[accents[i]];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">0{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">Four Dimensions of Board Contribution</span>
          </div>
          <h2 className="text-display-md mb-4">What the board service brings to the boardroom.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four structural contributions — each grounded in verified operational experience, each producing governance outcomes that conventional board members cannot achieve.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {CONTRIBUTIONS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <c.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Board Types Served</span>
          </div>
          <h2 className="text-display-md mb-4">Governance for every organizational structure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The board service spans multiple organizational structures — each presenting distinct governance challenges and each benefiting from the operational rigor brought to the boardroom.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BOARD_TYPES.map((b, i) => {
            const a = ACCENT[b.accent];
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{b.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Compass className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Governance Approach</span>
          </div>
          <h2 className="text-display-md mb-4">Five governance domains, one integrated method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The board contribution spans five governance domains, each connected to the others through an integrated framework. Governance is not siloed across domain-specific committees. It is integrated — a closed-loop system where insight from one domain strengthens governance in every other.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GOVERNANCE_DOMAINS.map((d, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <Scale className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{d.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Board Service Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">Six structural pillars of board service.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The board service is grounded in six structural pillars — each addressing a specific dimension of sovereign-grade governance, each producing measurable governance outcomes.
          </p>
        </div>
        <PillarGridBlock
          variant="numbered"
          columns={3}
          pillars={[
            { title: "Diagnostic Governance", body: "Understanding the organization's actual condition — the operational reality beneath quarterly metrics. Governance grounded in forensic understanding of the business, not in compliance with reporting cadences.", accent: "red" },
            { title: "Operational Excellence Oversight", body: "Translating oversight into measurable execution outcomes. The board does not monitor activities — it ensures the operational architecture produces verified performance against benchmarks.", accent: "purple" },
            { title: "Risk Architecture", body: "Clinical risk identification, evidence-based triage, protocol-driven management, continuous monitoring. The structural risk discipline that has produced zero security incidents across sovereign-scale operations.", accent: "cyan" },
            { title: "Cross-Domain Deliberation", body: "The Triple Threat — medicine, technology, and politics as one operating method. Board discussions consider operational, technological, political, and human dimensions simultaneously, not sequentially.", accent: "emerald" },
            { title: "The 70% Operating Model", body: "The operational case for board composition, talent strategy, and team development. Evidence-based, not ideological — replacing diversity rhetoric with diversity outcomes measured across five operating dimensions.", accent: "amber-gold" },
            { title: "Outcome Accountability", body: "Board effectiveness measured by organizational performance, not by activity volume. The same accountability that governs medical practice: the patient either recovers or does not. The organization either performs or does not.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Briefcase className="size-3.5 text-red" />
            <span className="text-overline text-red">Board Engagement Structure</span>
          </div>
          <h2 className="text-display-md mb-4">How a board engagement is structured.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every board service is structured around the organization's governance cadence, decision-making architecture, and accountability requirements. The structure adapts to the board's existing rhythm.
          </p>
        </div>
        <CardGridBlock
          variant="icon"
          columns={3}
          items={[
            { title: "Full Board Service", body: "Standing board member with full fiduciary obligations, attendance at scheduled sessions, committee participation, and continuous engagement with the organization's operational landscape. Typically two to four board meetings annually plus committee work.", accent: "red" },
            { title: "Advisory Board Position", body: "Non-fiduciary advisory role providing strategic counsel on operational, governance, and risk matters. Less formal than a full board position, but with the same operational authority and the same discretion standards.", accent: "purple" },
            { title: "Special Committee Chair", body: "Chair of a specific committee — audit, risk, technology, governance, or nominating. The role brings operational discipline to the committee's specific mandate and produces committee outputs that drive organizational performance.", accent: "cyan" },
            { title: "Crisis Governance Counsel", body: "Engaged during specific governance challenges — operational crises, leadership transitions, regulatory scrutiny, or strategic inflection points. The engagement is time-bound, focused, and outcome-verified.", accent: "emerald" },
            { title: "Governance Transformation", body: "Time-bound engagement to redesign the board's governance architecture — committee structures, information flows, decision frameworks, accountability mechanisms. The deliverable is a governance operating system that persists.", accent: "amber-gold" },
            { title: "Founder-to-Board Transition", body: "Specialized engagement supporting founder-led organizations transitioning to professionally operated governance. Bridges the gap between founder authority and institutional governance with the operational discipline both require.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Compass className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions boards ask before engaging.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Board service is selective. The questions boards ask reflect the seriousness of the engagement — the answer determines whether the operational perspective will strengthen the boardroom.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "What types of boards are served?", a: "Public company boards, private company boards, family office boards, sovereign and government boards, nonprofit and international organization boards, and advisory boards. Each board type presents distinct governance challenges and benefits from the operational perspective differently." },
            { q: "How is board service compensated?", a: "Compensation structures are discussed during the diagnostic phase, tailored to the engagement model (full board, advisory board, committee chair, crisis counsel, or transformation engagement). Equity, cash retainers, and hybrid structures are all considered based on the organization's governance norms and the engagement's scope." },
            { q: "What is the time commitment?", a: "Full board service typically involves two to four board meetings annually plus committee participation, preparation, and continuous engagement. Advisory positions are less formal. Special engagements are time-bound with specific deliverables. The commitment is calibrated to the role and the organization's governance cadence." },
            { q: "How is board independence maintained?", a: "Independence is structural. Board service includes no operational management authority, no commercial relationship with the organization beyond the board service itself, and no overlapping business interests. The clinical method's discipline of diagnosis produces independent perspective by construction." },
            { q: "What governance frameworks are applied?", a: "The clinical method is applied as a universal governance framework — diagnosis, triage, protocol, judgment, outcome accountability. Specific frameworks (audit, risk, technology, governance) are mapped to this universal structure. The framework adapts to the board's existing governance architecture rather than imposing external templates." },
            { q: "How are board conflicts handled?", a: "Conflicts are addressed through the same compartmentalized architecture that governs all engagements. The board service operates within the organization's existing conflict-of-interest policies. Where conflicts arise, the operational discipline of triage and judgment provides the structural resolution." },
            { q: "What is the typical board service tenure?", a: "Board service typically runs three to five years for full board positions, with renewal based on board performance assessment. Advisory and special engagements are time-bound by the engagement scope. Tenure is calibrated to the governance transformation the board seeks — long enough to produce structural change, bounded enough to ensure accountability." },
            { q: "How is the board service initiated?", a: "Board service begins with a diagnostic conversation with the board chair, the nominating committee, or the lead director. The conversation maps the board's current composition, governance challenges, and the specific perspective the board seeks. The engagement is designed around the diagnostic data." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Building2 className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about board service.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Board service commitments are evaluated based on the organization&apos;s mission, the governance challenge, and the alignment with operational expertise. The engagement begins with the same diagnostic process that governs all engagements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Inquire About Board Service
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/executive-advisory/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Briefcase className="size-4" />
                Executive Advisory
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Operational Consulting", description: "Sovereign-scale operational transformation across organizations.", href: "/services/operational-consulting/", icon: Activity, accent: "amber-gold" },
          { title: "Private Engagements", description: "Sovereign-level engagements by introduction only. Absolute discretion.", href: "/services/private-engagements/", icon: Lock, accent: "denim" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "cyan" },
        ]}
      />
    </>
  );
}
